// Pasted HTML, reduced to what an email body keeps: paragraphs, line breaks, bold, italic, links and lists.

import { normaliseUrl } from './logic';

const KEEP: Record<string, string> = {
  P: 'div',
  DIV: 'div',
  BR: 'br',
  B: 'b',
  STRONG: 'b',
  I: 'i',
  EM: 'i',
  U: 'u',
  A: 'a',
  UL: 'ul',
  OL: 'ol',
  LI: 'li',
  BLOCKQUOTE: 'blockquote',
};
const DROP = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'HEAD', 'META', 'LINK', 'TITLE', 'SVG', 'IMG', 'VIDEO', 'AUDIO', 'CANVAS', 'FORM', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA']);
const BLOCKS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TR', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'PRE', 'ADDRESS', 'FIGURE', 'DL', 'DT', 'DD', 'TABLE']);

function copyInto(src: Node, dst: Node, doc: Document): void {
  for (const child of Array.from(src.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      dst.appendChild(doc.createTextNode(child.textContent ?? ''));
      continue;
    }
    if (!(child instanceof Element)) continue;
    const tag = child.tagName.toUpperCase();
    if (DROP.has(tag)) continue;
    const keep = KEEP[tag];
    if (keep === 'a') {
      const href = normaliseUrl(child.getAttribute('href') ?? '');
      if (!href) {
        copyInto(child, dst, doc);
        continue;
      }
      const a = doc.createElement('a');
      a.setAttribute('href', href);
      copyInto(child, a, doc);
      dst.appendChild(a);
      continue;
    }
    // Google Docs wraps a whole paste in <b style="font-weight:normal">.
    const weight = child instanceof HTMLElement ? child.style.fontWeight : '';
    if (keep === 'b' && (weight === 'normal' || (Number(weight) > 0 && Number(weight) < 600))) {
      copyInto(child, dst, doc);
      continue;
    }
    if (keep) {
      const el = doc.createElement(keep);
      copyInto(child, el, doc);
      dst.appendChild(el);
      continue;
    }
    // Word processors mark bold and italic with styles on spans.
    const style = child instanceof HTMLElement ? child.style : null;
    let target: Node = dst;
    const wrap = (name: string) => {
      const el = doc.createElement(name);
      target.appendChild(el);
      target = el;
    };
    if (BLOCKS.has(tag)) wrap('div');
    if (/^H[1-6]$/.test(tag)) wrap('b');
    else if (style && (style.fontWeight === 'bold' || Number(style.fontWeight) >= 600)) wrap('b');
    if (style?.fontStyle === 'italic') wrap('i');
    copyInto(child, target, doc);
    if (tag === 'TD' || tag === 'TH') dst.appendChild(doc.createTextNode(' '));
  }
}

export function cleanPastedHtml(html: string): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const out = document.createElement('div');
  copyInto(parsed.body, out, document);
  return out.innerHTML;
}

/**
 * Stored HTML (a template, a scheduled email) in the editor's own shape: one div per line, with an empty line
 * between paragraphs so they keep their spacing.
 */
export function toEditorHtml(html: string): string {
  if (!/<p[\s>]/i.test(html)) return cleanPastedHtml(html);
  return cleanPastedHtml(html.replace(/<\/p>\s*(?=<(p|ul|ol|blockquote)[\s>])/gi, '</p><div><br></div>'));
}
